import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ApiError } from '@/lib/api'
import { formatMoney } from '@/format/money'
import { currentLocale } from '@/i18n'
import {
  allocatePayment,
  cancelInvoice,
  deallocate,
  fetchAdvances,
  fetchDunning,
  fetchInvoice,
  fetchSettlements,
  lineArithmetic,
  linesReconcile,
  sendInvoice,
  settledTotal,
  type DunningAction,
  type Invoice,
  type Payment,
  type Settlement,
} from './invoiceApi'
import type { CardTab, CardTabContext } from './types'

/**
 * Вкладки карточки счёта (тикет #72, ТЗ §4.3).
 *
 * ## Что здесь решается
 *
 * Счёт — не плоская строка, и каркас #66 его не покрывает: у него позиции с построчным
 * НДС, разнесённые на него платежи и лента напоминаний. Три вкладки отвечают на три
 * вопроса, которые бухгалтер задаёт в этом порядке: «из чего он сложился», «чем он
 * закрыт», «что мы делали, чтобы его закрыли».
 *
 * ## Общее правило про деньги на этих экранах
 *
 * **Ни одна итоговая цифра здесь не считается на клиенте.** `total`, `vatTotal`,
 * `outstanding`, `paidTotal` приходят с сервера. Арифметика в {@link lineArithmetic}
 * существует ровно для одного — **проверить**, что показанное сходится, и сказать вслух,
 * когда не сходится. Повторив формулу ради отображения, мы завели бы второй ответ на
 * денежный вопрос, и разошёлся бы он молча.
 */

/** Три состояния запроса одним компонентом: иначе их путают в каждой вкладке заново. */
function AsyncSection<T>({
  query,
  empty,
  children,
}: {
  query: { isLoading: boolean; error: unknown; data: T | undefined }
  empty: string
  children: (data: T) => React.ReactNode
}) {
  const { t } = useApp()

  if (query.isLoading) return <p>{t('app.loading')}</p>
  if (query.error) {
    return (
      <p role="alert">
        {query.error instanceof ApiError && query.error.isForbidden
          ? t('access.denied')
          : t('app.error')}
      </p>
    )
  }
  if (query.data === undefined) return <p className="muted">{empty}</p>
  return <>{children(query.data)}</>
}

/** Деньги с двумя знаками: счёт в копейках, и «1500» вместо «1500.00» читается как черновик. */
function money(value: number | null | undefined, currency?: string): string {
  // One formatter for every screen (#91); the tab has no hook here, so the stored language.
  return formatMoney(value, currency ?? null, currentLocale())
}

// ------------------------------------------------------------------ позиции и НДС

/**
 * Позиции счёта с построчным НДС (ТЗ §4.3).
 *
 * Показывает **и** сохранённые сервером суммы, **и** результат пересчёта по формуле — но
 * не рядом как две колонки, а как проверку: расхождение выводится предупреждением.
 * Две цифры в соседних ячейках заставляли бы читателя решать, какой верить, а он не
 * должен оказываться перед этим вопросом.
 */
function LinesPanel({ invoice }: { invoice: Invoice }) {
  const { t } = useApp()
  const lines = invoice.lines ?? []

  // Строки, чей сохранённый итог не сходится с пересчётом. Пустой список — норма;
  // непустой означает, что счёт показывает клиенту не то, что сложится из его строк.
  const drifting = useMemo(
    () =>
      lines.filter((line) => {
        const computed = lineArithmetic(line)
        return computed.total.toFixed(2) !== line.lineTotal.toFixed(2)
      }),
    [lines],
  )

  const reconciles = linesReconcile(invoice)

  if (lines.length === 0) {
    return <p className="muted">{t('card.inv.lines.empty')}</p>
  }

  return (
    <div className="card-gap">
      {/* Итог, не равный сумме строк, назван вслух: клиент складывает строки, и молчание
          об этом расхождении обнаружит он, а не мы. */}
      {!reconciles && (
        <p className="notice notice-error" role="alert">
          {t('card.inv.lines.mismatch')}
        </p>
      )}
      {drifting.length > 0 && (
        <p className="notice notice-error" role="alert">
          {t('card.inv.lines.lineMismatch', { count: drifting.length })}
        </p>
      )}

      <table className="card-table">
        <thead>
          <tr>
            <th>{t('card.inv.line.description')}</th>
            <th>{t('card.inv.line.quantity')}</th>
            <th>{t('card.inv.line.unitPrice')}</th>
            <th>{t('card.inv.line.discount')}</th>
            <th>{t('card.inv.line.net')}</th>
            <th>{t('card.inv.line.vatRate')}</th>
            <th>{t('card.inv.line.vatAmount')}</th>
            <th>{t('card.inv.line.total')}</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const computed = lineArithmetic(line)
            return (
              <tr key={line.id}>
                <td>{line.description}</td>
                <td>{line.quantity}</td>
                <td>{money(line.unitPrice)}</td>
                {/* Скидка процентом, а не суммой: так она задана, и пересчёт в рубли
                    здесь скрыл бы, что НДС начисляется на сумму СО скидкой. */}
                <td>{line.discountPercent > 0 ? `${line.discountPercent}%` : '—'}</td>
                <td>{money(computed.net)}</td>
                <td>{`${line.vatRate}%`}</td>
                <td>{money(line.vatAmount)}</td>
                <td>{money(line.lineTotal)}</td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          {/* Итоги — с сервера, а не сумма отрисованных строк: сложив на клиенте, мы бы
              показали «правильный» итог поверх неправильного и спрятали расхождение. */}
          <tr>
            <th colSpan={4}>{t('card.inv.total.subtotal')}</th>
            <td>{money(invoice.subtotal)}</td>
            <td colSpan={3} />
          </tr>
          {invoice.discountTotal > 0 && (
            <tr>
              <th colSpan={4}>{t('card.inv.total.discount')}</th>
              <td colSpan={4}>{money(invoice.discountTotal)}</td>
            </tr>
          )}
          <tr>
            <th colSpan={4}>{t('card.inv.total.vat')}</th>
            <td colSpan={4}>{money(invoice.vatTotal)}</td>
          </tr>
          <tr>
            <th colSpan={4}>{t('card.inv.total.total')}</th>
            <td colSpan={4}>
              <strong>{money(invoice.total, invoice.currency)}</strong>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

// ------------------------------------------------------------------ оплаты

/**
 * Разнесение оплаты на счёт.
 *
 * Форма предлагает **аванс** контрагента, а не свободный ввод идентификатора платежа:
 * бухгалтер разносит то, что уже пришло, и заставлять его копировать UUID из соседнего
 * раздела значит гарантировать опечатку в денежной операции.
 *
 * Сумма подставляется как минимум из «сколько осталось на счёте» и «сколько не разнесено
 * с платежа»: любая большая домен отвергнет, и предлагать её значит обещать операцию,
 * которая не пройдёт.
 */
function AllocateForm({
  invoice,
  advances,
  onDone,
}: {
  invoice: Invoice
  advances: Payment[]
  onDone: () => void
}) {
  const { t } = useApp()
  const [paymentId, setPaymentId] = useState('')
  const [amount, setAmount] = useState('')
  const [failure, setFailure] = useState<string | null>(null)

  const selected = advances.find((payment) => payment.id === paymentId) ?? null

  // Потолок — минимум из двух остатков. Считается для ПОДСКАЗКИ, не для проверки:
  // отказать обязан домен, иначе клиентская формула станет вторым местом, где решается,
  // сколько можно разнести.
  const suggested = selected
    ? Math.min(invoice.outstanding, selected.unallocatedAmount)
    : invoice.outstanding

  const mutation = useMutation({
    mutationFn: () => allocatePayment(paymentId, invoice.id, Number(amount)),
    onSuccess: () => {
      setFailure(null)
      setPaymentId('')
      setAmount('')
      onDone()
    },
    onError: (error) =>
      setFailure(error instanceof ApiError ? error.message : t('app.error')),
  })

  const submit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault()
      setFailure(null)
      if (!paymentId) {
        setFailure(t('card.inv.allocate.noPayment'))
        return
      }
      const value = Number(amount)
      // Ноль и отрицательное отсекаются здесь, потому что это не денежное правило, а
      // отсутствие ввода: пустое поле иначе уедет на сервер как NaN.
      if (!Number.isFinite(value) || value <= 0) {
        setFailure(t('card.inv.allocate.badAmount'))
        return
      }
      mutation.mutate()
    },
    [amount, mutation, paymentId, t],
  )

  // Счёт, на который разносить нельзя (отменён, уже оплачен), формы не получает: кнопка,
  // ведущая к гарантированному отказу, учит игнорировать отказы.
  if (!invoice.payable) {
    return <p className="muted">{t('card.inv.allocate.notPayable')}</p>
  }

  return (
    <form className="card-gap" onSubmit={submit}>
      {failure && (
        <p className="notice notice-error" role="alert">
          {failure}
        </p>
      )}

      <label>
        {t('card.inv.allocate.payment')}
        <select
          value={paymentId}
          onChange={(event) => {
            setPaymentId(event.target.value)
            setAmount('')
          }}
        >
          <option value="">{t('card.inv.allocate.choose')}</option>
          {advances.map((payment) => (
            <option key={payment.id} value={payment.id}>
              {`${payment.number ?? payment.id.slice(0, 8)} · ${payment.paymentDate} · ${money(
                payment.unallocatedAmount,
                payment.currency,
              )}`}
            </option>
          ))}
        </select>
      </label>

      {advances.length === 0 && <p className="muted">{t('card.inv.allocate.noAdvances')}</p>}

      <label>
        {t('card.inv.allocate.amount')}
        <input
          type="number"
          step="0.01"
          min="0"
          value={amount}
          placeholder={suggested.toFixed(2)}
          onChange={(event) => setAmount(event.target.value)}
        />
      </label>

      <button type="submit" disabled={mutation.isPending}>
        {mutation.isPending ? t('card.inv.allocate.saving') : t('card.inv.allocate.submit')}
      </button>
    </form>
  )
}

/**
 * Чем закрыт счёт: разнесения вместе с платежами.
 *
 * Сумма строк сверяется с `paidTotal` счёта. Это не украшение: расхождение означает, что
 * денормализованная цифра на счёте разошлась с журналом разнесений, и увидеть это надо
 * здесь, а не в квартальной сверке.
 */
function SettlementsPanel({ invoice, recordId }: { invoice: Invoice; recordId: string }) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()

  // Право читается ЗДЕСЬ: `extraTabs` — статический массив, собранный на импорте модуля,
  // и права, переданные параметром, застыли бы на первом вошедшем пользователе.
  const mayReadPayments = can(user, 'payments.payment', 'read')
  const mayAllocate = can(user, 'payments.payment', 'update')

  const settlements = useQuery({
    queryKey: ['invoice-settlements', recordId],
    // Без права запрос не отправляется: он всё равно вернёт 403, и единственным
    // результатом был бы красный текст вместо объяснения. Запрет остаётся на сервере.
    enabled: mayReadPayments,
    queryFn: ({ signal }) => fetchSettlements(recordId, signal),
    retry: false,
  })

  const advances = useQuery({
    queryKey: ['counterparty-advances', invoice.counterpartyId],
    enabled: mayAllocate && invoice.payable,
    queryFn: ({ signal }) => fetchAdvances(invoice.counterpartyId, signal),
    retry: false,
  })

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['invoice-settlements', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['invoice', recordId] })
    void queryClient.invalidateQueries({
      queryKey: ['counterparty-advances', invoice.counterpartyId],
    })
  }, [invoice.counterpartyId, queryClient, recordId])

  const removal = useMutation({
    mutationFn: (allocationId: string) => deallocate(allocationId),
    onSuccess: refresh,
  })

  if (!mayReadPayments) {
    return <p className="muted">{t('card.inv.settlements.needsPayments')}</p>
  }

  return (
    <div className="card-gap">
      {/* Долг — первым: карточку счёта открывают, чтобы узнать, сколько ещё должны. */}
      <dl className="card-form">
        <div className="card-field">
          <dt>{t('card.inv.total.total')}</dt>
          <dd>{money(invoice.total, invoice.currency)}</dd>
        </div>
        <div className="card-field">
          <dt>{t('card.inv.paid')}</dt>
          <dd>{money(invoice.paidTotal, invoice.currency)}</dd>
        </div>
        <div className="card-field">
          <dt>{t('card.inv.outstanding')}</dt>
          <dd>
            <strong>{money(invoice.outstanding, invoice.currency)}</strong>
          </dd>
        </div>
      </dl>

      <AsyncSection query={settlements} empty={t('card.inv.settlements.empty')}>
        {(rows: Settlement[]) => (
          <>
            {/* Сумма разнесений против paid_total счёта. Расхождение — порча данных, и
                показать его надо тому, кто смотрит на счёт, а не найти через квартал. */}
            {rows.length > 0 && settledTotal(rows).toFixed(2) !== invoice.paidTotal.toFixed(2) && (
              <p className="notice notice-error" role="alert">
                {t('card.inv.settlements.mismatch', {
                  allocated: money(settledTotal(rows), invoice.currency),
                  paid: money(invoice.paidTotal, invoice.currency),
                })}
              </p>
            )}

            {rows.length === 0 ? (
              <p className="muted">{t('card.inv.settlements.empty')}</p>
            ) : (
              <table className="card-table">
                <thead>
                  <tr>
                    <th>{t('card.inv.settlement.payment')}</th>
                    <th>{t('card.inv.settlement.date')}</th>
                    <th>{t('card.inv.settlement.method')}</th>
                    <th>{t('card.inv.settlement.amount')}</th>
                    <th>{t('card.inv.settlement.rule')}</th>
                    {mayAllocate && <th>{t('card.inv.settlement.actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.allocation.id}>
                      <td>
                        {/* Битая ссылка показывается пустотой, а не выдуманным номером:
                            платёж, которого нет, — порча данных, и заполнять её значит
                            прятать. */}
                        {row.payment
                          ? (row.payment.number ?? row.payment.id.slice(0, 8))
                          : t('card.inv.settlement.orphan')}
                      </td>
                      <td>{row.payment?.paymentDate ?? '—'}</td>
                      <td>
                        {row.payment ? t(`payment.method.${row.payment.method}`) : '—'}
                      </td>
                      <td>{money(row.allocation.amount, invoice.currency)}</td>
                      <td>{t(`allocation.rule.${row.allocation.rule}`)}</td>
                      {mayAllocate && (
                        <td>
                          <button
                            type="button"
                            disabled={removal.isPending}
                            onClick={() => removal.mutate(row.allocation.id)}
                          >
                            {t('card.inv.settlement.remove')}
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </AsyncSection>

      {mayAllocate && (
        <>
          <h3>{t('card.inv.allocate.title')}</h3>
          <AllocateForm
            invoice={invoice}
            advances={advances.data ?? []}
            onDone={refresh}
          />
        </>
      )}
    </div>
  )
}

// ------------------------------------------------------------------ напоминания

/**
 * Лента напоминаний из `dunning_action` (ТЗ §4.3).
 *
 * **Все** ступени, а не последняя. Обход дебиторки записывает каждую пропущенную ступень,
 * а не только свежую — иначе счёт, по которому задача не отрабатывала неделю, прыгал бы с
 * первого напоминания сразу к юристам. Показать одну последнюю значит скрыть, почему счёт
 * там оказался: именно история объясняет, что клиенту уже писали трижды.
 */
function DunningPanel({ recordId }: { recordId: string }) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['invoice-dunning', recordId],
    queryFn: ({ signal }) => fetchDunning(recordId, signal),
    retry: false,
  })

  return (
    <AsyncSection query={query} empty={t('card.inv.dunning.empty')}>
      {(actions: DunningAction[]) =>
        actions.length === 0 ? (
          // «Ступеней не было» — это факт о счёте (он не просрочен или его не трогали),
          // а не отсутствие данных, и сказать это надо словами.
          <p className="muted">{t('card.inv.dunning.empty')}</p>
        ) : (
          <table className="card-table">
            <thead>
              <tr>
                <th>{t('card.inv.dunning.step')}</th>
                <th>{t('card.inv.dunning.kind')}</th>
                <th>{t('card.inv.dunning.scheduled')}</th>
                <th>{t('card.inv.dunning.executed')}</th>
                <th>{t('card.inv.dunning.result')}</th>
              </tr>
            </thead>
            <tbody>
              {/* Порядок — по дням просрочки: лестница читается снизу вверх по строгости,
                  и сортировка по времени записи перемешала бы её при догоняющем обходе. */}
              {[...actions]
                .sort((a, b) => a.daysOverdue - b.daysOverdue)
                .map((action) => (
                  <tr key={action.id}>
                    <td>{t('card.inv.dunning.days', { days: action.daysOverdue })}</td>
                    <td>{t(`dunning.kind.${action.kind}`)}</td>
                    <td>{action.scheduledOn}</td>
                    <td>
                      {/* Запланировано и выполнено — разные факты: ступень, записанная
                          обходом, но не отработанная, это то, что надо сделать, а не то,
                          что сделано. */}
                      {action.executed ? action.executedAt : t('card.inv.dunning.pending')}
                    </td>
                    <td>{action.result ?? '—'}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        )
      }
    </AsyncSection>
  )
}

// ------------------------------------------------------------------ действия

/**
 * Отправка, отмена и повторная выписка (ТЗ §4.3).
 *
 * Каждое действие сопровождается тем, чего оно стоит: отмена требует причины, потому что
 * отменённый счёт остаётся в реестре навсегда и через год «почему» спросят у того, кто не
 * помнит.
 *
 * Доступность действия определяется **статусом счёта с сервера**, а не догадкой: домен
 * решает, что счёт в `paid` отменять нельзя, и вторая такая проверка здесь разошлась бы с
 * ним — в ту сторону, где кнопка есть, а операции нет.
 */
function InvoiceActions({ invoice, recordId }: { invoice: Invoice; recordId: string }) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const [reason, setReason] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const mayUpdate = can(user, 'billing.invoice', 'update')

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['invoice', recordId] })
    void queryClient.invalidateQueries({ queryKey: ['list', 'billing.invoice'] })
  }, [queryClient, recordId])

  const send = useMutation({
    mutationFn: () => sendInvoice(recordId),
    onSuccess: () => {
      setFailure(null)
      setNotice(t('card.inv.action.sent'))
      refresh()
    },
    onError: (error) =>
      setFailure(error instanceof ApiError ? error.message : t('app.error')),
  })

  const cancel = useMutation({
    mutationFn: () => cancelInvoice(recordId, reason),
    onSuccess: () => {
      setFailure(null)
      setNotice(t('card.inv.action.cancelled'))
      setReason('')
      refresh()
    },
    onError: (error) =>
      setFailure(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (!mayUpdate) return null

  const sendable = invoice.status === 'draft'
  const cancellable = invoice.status !== 'cancelled' && invoice.status !== 'paid'

  return (
    <div className="card-gap">
      {failure && (
        <p className="notice notice-error" role="alert">
          {failure}
        </p>
      )}
      {notice && (
        <p className="notice" role="status">
          {notice}
        </p>
      )}

      <div className="card-actions">
        <button type="button" disabled={!sendable || send.isPending} onClick={() => send.mutate()}>
          {t('card.inv.action.send')}
        </button>
      </div>

      {/* Уже отправленный счёт: сказать, почему кнопка недоступна, а не просто погасить
          её. Погашенная кнопка без объяснения читается как поломка. */}
      {!sendable && <p className="muted">{t('card.inv.action.sendUnavailable')}</p>}

      {cancellable ? (
        <div className="card-gap">
          <label>
            {t('card.inv.action.reason')}
            <input
              type="text"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <button
            type="button"
            // Причина обязательна: домен отвергнет пустую, и подставить сюда «отменён»
            // значило бы записать в реестр объяснение, которого никто не давал.
            disabled={reason.trim() === '' || cancel.isPending}
            onClick={() => cancel.mutate()}
          >
            {t('card.inv.action.cancel')}
          </button>
          {reason.trim() === '' && <p className="muted">{t('card.inv.action.reasonRequired')}</p>}
        </div>
      ) : (
        <p className="muted">{t('card.inv.action.cancelUnavailable')}</p>
      )}

      {/* Повторная выписка названа вслух как отсутствующая: домен её не публикует.
          Кнопка, «переоформляющая» счёт удалением и созданием нового, выпустила бы
          документ с новым номером под видом того же — а номер уже у клиента. */}
      <h3>{t('card.inv.action.reissue')}</h3>
      <p className="muted">{t('card.inv.action.reissueMissing')}</p>
    </div>
  )
}

// ------------------------------------------------------------------ сборка вкладок

/** Загружает счёт и отдаёт его панели: три вкладки читают один и тот же документ. */
function WithInvoice({
  recordId,
  children,
}: {
  recordId: string
  children: (invoice: Invoice) => React.ReactNode
}) {
  const { t } = useApp()
  const query = useQuery({
    queryKey: ['invoice', recordId],
    queryFn: ({ signal }) => fetchInvoice(recordId, signal),
    retry: false,
  })

  return (
    <AsyncSection query={query} empty={t('card.inv.missing')}>
      {(invoice: Invoice) => <>{children(invoice)}</>}
    </AsyncSection>
  )
}

/** Позиции, НДС, скидки, итог. */
export function invoiceLinesTab(): CardTab {
  return {
    id: 'invoice-lines',
    labelKey: 'card.tab.invoiceLines',
    render: (context: CardTabContext) => (
      <WithInvoice recordId={context.recordId}>
        {(invoice) => <LinesPanel invoice={invoice} />}
      </WithInvoice>
    ),
  }
}

/** Оплаты: чем закрыт счёт и разнесение новых поступлений. */
export function invoiceSettlementsTab(): CardTab {
  return {
    id: 'invoice-settlements',
    labelKey: 'card.tab.invoiceSettlements',
    render: (context: CardTabContext) => (
      <WithInvoice recordId={context.recordId}>
        {(invoice) => <SettlementsPanel invoice={invoice} recordId={context.recordId} />}
      </WithInvoice>
    ),
  }
}

/** Лента напоминаний и действия над счётом. */
export function invoiceDunningTab(): CardTab {
  return {
    id: 'invoice-dunning',
    labelKey: 'card.tab.invoiceDunning',
    render: (context: CardTabContext) => <DunningPanel recordId={context.recordId} />,
  }
}

/** Отправка, отмена, повторная выписка. */
export function invoiceActionsTab(): CardTab {
  return {
    id: 'invoice-actions',
    labelKey: 'card.tab.invoiceActions',
    render: (context: CardTabContext) => (
      <WithInvoice recordId={context.recordId}>
        {(invoice) => <InvoiceActions invoice={invoice} recordId={context.recordId} />}
      </WithInvoice>
    ),
  }
}

/** Все вкладки счёта. Права читают сами вкладки — см. `SettlementsPanel`. */
export function invoiceTabs(): CardTab[] {
  return [invoiceLinesTab(), invoiceSettlementsTab(), invoiceDunningTab(), invoiceActionsTab()]
}

export { LinesPanel, SettlementsPanel, DunningPanel, InvoiceActions, AllocateForm }
